import { apiFetch } from './client.js';
import type { SignatureCodes } from './coach-signature.js';

/**
 * An athlete's Performance Signature as the server sends it (stage 2): the
 * codes, and only the content those codes select, word for word from the
 * sources. Which sections are present follows the rules ruled 25 October; the
 * screens draw what is there and never decide it again.
 */

type GetToken = () => Promise<string | null>;

export interface ProfileSide {
  side: 'L' | 'R';
  content: {
    profile: { code: string; name: string; family: string };
    attributes: { key: string; icon: string; title: string; text: string }[];
    hitting: { phase: string; source: string; text: string; subtext: string }[];
    images: { figure: string; zone: string };
  };
}

export interface MotivationSection {
  position: 1 | 2;
  code: string;
  content: { code: string; title: string; needs: string[]; guidance: string[] };
}

export interface SignatureView {
  codes: SignatureCodes;
  updated_at: string;
  sections: {
    profile: { code: string; sides: ProfileSide[] } | null;
    motivations: MotivationSection[];
    brise: { title: string; steps: [string, string][] };
  };
}

/** The signed-in athlete's own; null until the coach has entered a code. */
export function readAthleteSignature(getToken: GetToken): Promise<{ signature: SignatureView | null }> {
  return apiFetch('/api/athlete/signature', getToken);
}

/** The signed-in parent's or coach's own, with their person id (25 October). */
export function readOwnSignature(getToken: GetToken): Promise<{ client_id: string; signature: SignatureView | null }> {
  return apiFetch('/api/me/signature', getToken);
}

/** A child's, for either parent (stage 4b); another family's child is 404. */
export function readFamilySignature(getToken: GetToken, clientId: string): Promise<{ signature: SignatureView | null }> {
  return apiFetch(`/api/people/${encodeURIComponent(clientId)}/signature`, getToken);
}

/**
 * The card images, served as files beside the app (public/signature): the same
 * for every athlete, so they hold nothing about anyone. Each zone panel serves
 * two profiles - C with R, D with G - as on the approved all-zones sheet.
 */
export function signatureImage(name: string): string {
  const zone = /^zone-([cdgr])(\d)-(lhb|rhb)$/.exec(name);
  if (!zone) return `/signature/${name}.png`;
  const [, family, n, side] = zone;
  const pair = family === 'c' || family === 'r' ? `c${n}-r${n}` : `d${n}-g${n}`;
  return `/signature/zone-${pair}-${side}.png`;
}
